import { instrumentRunQueryKeys } from '@/config/queryKeys';
import { apiClient } from '@/lib/api-client';
import { fetchInstrumentRun } from '@/services/instrumentRuns';
import { type UseMutationResult, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import type { InstrumentRunPublic } from './types';

/**
 * Hook: useInstrumentRun
 * - Suspense-enabled query to fetch a single instrument run.
 * - Keep this thin: callers control caching and rendering behavior.
 */
export function useInstrumentRun(selectedOrgId: string, instrumentRunId: string) {
  return useSuspenseQuery<InstrumentRunPublic>({
    queryKey: instrumentRunQueryKeys.detail(selectedOrgId, instrumentRunId),
    queryFn: () => fetchInstrumentRun(selectedOrgId, instrumentRunId),
  });
}

/**
 * Tighten the update payload shape to only fields we intend to send.
 * This prevents accidentally sending readonly or unrelated fields.
 */
type UpdatePayload = Partial<
  Pick<InstrumentRunPublic, 'name' | 'description' | 'type' | 'species' | 'num_samples' | 'samples' | 'status'>
>;

export type UpdateVariables = { description: string };
export type MutationContext = { previousData?: InstrumentRunPublic };

/**
 * Hook: useUpdateDescription
 *
 * - Provides a focused mutation for updating only the description (and required companion fields).
 * - Uses optimistic updates to update the cached instrument run immediately for snappy UI.
 * - Reconciles on error by restoring previous cache.
 *
 * Perf notes:
 * - We cache the `queryKey` locally and reuse it across mutation lifecycle to avoid recomputing.
 * - The mutation variables are strictly typed as `{ description: string }` which is clearer than a loose record.
 */
export function useUpdateDescription(
  selectedOrgId: string,
  instrumentRunId: string,
  data?: InstrumentRunPublic,
): UseMutationResult<InstrumentRunPublic, Error, UpdateVariables, MutationContext> {
  const queryClient = useQueryClient();
  const queryKey = instrumentRunQueryKeys.detail(selectedOrgId, instrumentRunId);

  return useMutation<InstrumentRunPublic, Error, UpdateVariables, MutationContext>({
    // Named function for better stack traces in dev tools
    mutationFn: async (variables: UpdateVariables) => {
      if (!data) throw new Error('No instrument run data available to build update payload');

      // Build a minimal payload from allowed fields. This avoids accidentally sending extra properties.
      const { name, type, species, num_samples, samples = [], status } = data;

      const updatePayload: UpdatePayload = {
        name,
        description: variables.description,
        type,
        species,
        num_samples,
        samples,
      };

      // Preserve status only when it's not SUBMITTED (existing behaviour)
      if (status !== 'SUBMITTED') {
        updatePayload.status = status;
      }

      const response = await apiClient.put<InstrumentRunPublic>(
        `/orgs/${selectedOrgId}/instrument_runs/${instrumentRunId}`,
        updatePayload,
      );

      if (!response) {
        throw new Error('Failed to update instrument run');
      }

      return response;
    },

    // Optimistic update: update cache immediately and return previous data for rollback.
    onMutate: async (variables: UpdateVariables) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<InstrumentRunPublic>(queryKey);

      queryClient.setQueryData<InstrumentRunPublic>(queryKey, old => {
        if (!old) return old;
        // Only change the description field optimistically.
        return { ...old, description: variables.description };
      });

      return { previousData };
    },

    // Roll back to previous data if mutation fails.
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },

    // Replace cache with the server's authoritative response on success.
    onSuccess: updated => {
      queryClient.setQueryData(queryKey, updated);
    },
  });
}
